import { ChevronLeft, ChevronRight, FileSpreadsheet, FileText, Info } from 'lucide-react';
import React from 'react';
import { CustomSelect } from '../../shared/components/CustomSelect';
import { WorkLogEntry } from '../../shared/types';
import { dateUtils } from '../../shared/utils';

const PAGINATION_WINDOW = 7; // number of page links to show (Google-style)

function getPageWindow(
  totalPages: number,
  current: number,
  windowSize: number = PAGINATION_WINDOW
): (number | 'ellipsis')[] {
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const result: (number | 'ellipsis')[] = [];
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(totalPages, start + windowSize - 1);
  if (end - start + 1 < windowSize) {
    start = Math.max(1, end - windowSize + 1);
  }
  if (start > 1) {
    result.push(1);
    if (start > 2) result.push('ellipsis');
  }
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  if (end < totalPages) {
    if (end < totalPages - 1) result.push('ellipsis');
    result.push(totalPages);
  }
  return result;
}

interface WorkLogTableProps {
  data: WorkLogEntry[];
  onExportCSV: () => void;
  onExportExcel: () => void;
  /** When set, table is in paginated mode: show totalCount and pagination controls */
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
  loading?: boolean;
}

export const WorkLogTable: React.FC<WorkLogTableProps> = ({
  data,
  onExportCSV,
  onExportExcel,
  totalCount,
  page = 1,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [50, 100, 200, 500],
  loading = false,
}) => {
  const isPaginated = totalCount !== undefined && totalCount > 0 && onPageChange;
  const totalPages = isPaginated ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
  const from = isPaginated ? (page - 1) * pageSize + 1 : 1;
  const to = isPaginated ? Math.min(page * pageSize, totalCount) : data.length;
  const pageNumbers = getPageWindow(totalPages, page);
  const getStatusClass = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('in progress') || s === 'in progress') return 'status-in-progress';
    if (s.includes('to do') || s === 'to do') return 'status-to-do';
    if (s.includes('done')) return 'status-done';
    if (s.includes('in testing') || s.includes('testing')) return 'status-in-testing';
    if (s.includes('to verify') || s === 'to verify') return 'status-to-verify';
    if (s.includes('merged')) return 'status-merged';
    if (s.includes('verified')) return 'status-verified';
    if (s.includes('cancelled') || s.includes('canceled')) return 'status-cancelled';
    if (s.includes('code review') || s.includes('review')) return 'status-code-review';
    return 'status-to-do';
  };

  const getTypeClass = (type: string) => {
    const t = (type || '').toLowerCase();
    if (t.includes('initiative')) return 'type-initiative';
    if (t.includes('epic')) return 'type-epic';
    if (t.includes('bug')) return 'type-bug';
    if (t.includes('security')) return 'type-security-issue';
    if (t.includes('story')) return 'type-story';
    if (t.includes('ui/ux')) return 'type-ui-ux';
    if (t.includes('sub-task') || t.includes('subtask')) return 'type-sub-task';
    if (t.includes('task')) return 'type-task';
    return 'type-task';
  };

  const totalTime = data.reduce((sum, entry) => sum + entry.timeSpent, 0);

  return (
    <div className="worklog-table-container">
      <div className="table-header">
        <h3>
          Work Log Data ({isPaginated ? totalCount : data.length} entries)
          <span
            style={{ display: 'flex', alignItems: 'center', cursor: 'help', marginLeft: '0.5rem' }}
            title="Detailed table showing all work log entries with time spent, status, and task information"
          >
            <Info size={16} style={{ color: '#6b7280' }} />
          </span>
        </h3>
        <div className="export-buttons">
          <button
            className="btn btn-secondary export-btn"
            onClick={onExportCSV}
            disabled={data.length === 0 || loading}
            title="Download in CSV format"
          >
            <FileText size={16} />
            CSV
          </button>
          <button
            className="btn btn-secondary export-btn"
            onClick={onExportExcel}
            disabled={data.length === 0 || loading}
            title="Download in Excel format"
          >
            <FileSpreadsheet size={16} />
            Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="worklog-table-loading">
          <span>Loading...</span>
        </div>
      )}

      <div className="table-container">
        <table className="worklog-table">
          <thead>
            <tr>
              <th title="Name of the developer who logged the time">Author</th>
              <th title="Jira task identifier">Key</th>
              <th title="Fix version assigned to the task">Fix Version</th>
              <th title="Date when the work was performed">Start Date</th>
              <th title="Hours spent on the task">Time Spent (hrs)</th>
              <th title="Current status of the task">Status</th>
              <th title="Type of work item">Type</th>
              <th title="Brief description of the task">Summary</th>
            </tr>
          </thead>
          <tbody>
            {data.map((entry, index) => (
              <tr key={index}>
                <td>{entry.author}</td>
                <td>
                  <a
                    href={`https://your-jira-instance.atlassian.net/browse/${entry.key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="task-link"
                  >
                    {entry.key}
                  </a>
                </td>
                <td>
                  {entry.fixVersion ? (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: 'var(--indigo, #6366f1)',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}
                    >
                      {entry.fixVersion}
                    </span>
                  ) : (
                    <span style={{ color: '#6b7280', fontStyle: 'italic' }}>—</span>
                  )}
                </td>
                <td>{dateUtils.formatDisplayDate(entry.startDate)}</td>
                <td className="time-spent">{entry.timeSpent.toFixed(1)}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(entry.status)}`}>
                    {entry.status}
                  </span>
                </td>
                <td>
                  <span className={`type-badge ${getTypeClass(entry.type)}`}>{entry.type}</span>
                </td>
                <td className="summary-cell" title={entry.summary}>
                  {entry.summary.length > 50
                    ? `${entry.summary.substring(0, 50)}...`
                    : entry.summary}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={4}>
                <strong>Total</strong>
              </td>
              <td className="total-time" title="Sum of all time spent across all entries">
                <strong>{totalTime.toFixed(1)} hrs</strong>
              </td>
              <td colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {isPaginated && (
        <div className="worklog-table-pagination">
          <div className="worklog-table-pagination__left">
            <span className="worklog-table-pagination__range">
              {from}–{to} of {totalCount}
            </span>
            {onPageSizeChange && pageSizeOptions.length > 0 && (
              <label className="worklog-table-pagination__perpage">
                <span className="worklog-table-pagination__perpage-label">Per page:</span>
                <CustomSelect
                  value={String(pageSize)}
                  options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
                  onChange={(val) => onPageSizeChange(Number(val))}
                  disabled={loading}
                />
              </label>
            )}
          </div>
          <div className="worklog-table-pagination__controls">
            <button
              type="button"
              className="btn btn-secondary worklog-table-pagination__btn worklog-table-pagination__btn--nav"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
              Prev
            </button>
            <button
              type="button"
              className="btn btn-secondary worklog-table-pagination__btn worklog-table-pagination__btn--nav worklog-table-pagination__btn--jump"
              onClick={() => onPageChange(Math.max(1, page - 10))}
              disabled={page <= 1 || loading}
              aria-label="10 pages back"
              title="10 pages back"
            >
              −10
            </button>
            <div className="worklog-table-pagination__numbers">
              {pageNumbers.map((item, idx) =>
                item === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} className="worklog-table-pagination__ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`worklog-table-pagination__num ${
                      item === page ? 'worklog-table-pagination__num--current' : ''
                    }`}
                    onClick={() => onPageChange(item)}
                    disabled={loading}
                    aria-label={`Page ${item}`}
                    aria-current={item === page ? 'page' : undefined}
                  >
                    {item}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              className="btn btn-secondary worklog-table-pagination__btn worklog-table-pagination__btn--nav worklog-table-pagination__btn--jump"
              onClick={() => onPageChange(Math.min(totalPages, page + 10))}
              disabled={page >= totalPages || loading}
              aria-label="10 pages forward"
              title="10 pages forward"
            >
              +10
            </button>
            <button
              type="button"
              className="btn btn-secondary worklog-table-pagination__btn worklog-table-pagination__btn--nav"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || loading}
              aria-label="Next page"
            >
              Next
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkLogTable;
